import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Download as DownloadIcon, Send as SendIcon, Block as VoidIcon } from '@mui/icons-material';
import { makeApiCall } from '../../config/api';
import { triggerSignedUrlDownload } from '../../utils/download';
import { ACTIVE_ESIGN_STATUSES, apiError, esignStatusColor, formatDateTime } from './esignUtils';

const Row = ({ label, children }) => (
  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0, sm: 2 }} sx={{ py: 0.5 }}>
    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 150 }}>{label}</Typography>
    <Typography variant="body2" component="div" sx={{ wordBreak: 'break-word' }}>{children}</Typography>
  </Stack>
);

/**
 * Status, history and actions for one signing request (TKT-401).
 * onChanged() is called after a resend or void so lists can refresh.
 */
const EnvelopeDetailDialog = ({ envelopeId, open, onClose, onChanged }) => {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [voidOpen, setVoidOpen] = useState(false);
  const [voidReason, setVoidReason] = useState('');

  const load = useCallback(async () => {
    if (!envelopeId) return;
    setLoading(true);
    setError('');
    try {
      const res = await makeApiCall(`api/esign/envelopes/${envelopeId}`);
      if (!res.ok) throw new Error(await apiError(res, t('esign.detail.loadFailed')));
      setData(await res.json());
    } catch (e) {
      setError(e.message || t('esign.detail.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [envelopeId, t]);

  useEffect(() => {
    if (open) {
      setData(null);
      setNotice('');
      load();
    }
  }, [open, load]);

  const runAction = async (path, body, successMessage) => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const res = await makeApiCall(`api/esign/envelopes/${envelopeId}/${path}`, {
        method: 'POST',
        body: JSON.stringify(body || {}),
      });
      if (!res.ok) throw new Error(await apiError(res, res.statusText));
      setNotice(successMessage);
      await load();
      if (onChanged) onChanged();
    } catch (e) {
      setError(t('esign.detail.actionFailed', { error: e.message }));
    } finally {
      setBusy(false);
    }
  };

  const download = async (which) => {
    setError('');
    try {
      const res = await makeApiCall(`api/esign/envelopes/${envelopeId}/document?which=${which}`);
      if (!res.ok) throw new Error(await apiError(res, res.statusText));
      const { url } = await res.json();
      // The signed URL already names the file for download.
      triggerSignedUrlDownload(url);
    } catch (e) {
      setError(t('esign.detail.actionFailed', { error: e.message }));
    }
  };

  const envelope = data?.envelope;
  const signer = data?.recipients?.[0];
  const active = envelope && ACTIVE_ESIGN_STATUSES.includes(envelope.status);
  const count = (type) => data?.fieldSummary?.find((f) => f.type === type)?.count || 0;
  const otherCount = (data?.fieldSummary || [])
    .filter((f) => !['signature', 'initials', 'date_signed', 'envelope_id'].includes(f.type))
    .reduce((sum, f) => sum + f.count, 0);
  const lang = i18n.language;

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {t('esign.detail.title')}
          {envelope && (
            <Chip size="small" color={esignStatusColor(envelope.status)} label={t(`esign.status.${envelope.status}`)} />
          )}
        </DialogTitle>
        <DialogContent dividers>
          {loading && !data && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          )}
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {notice && <Alert severity="success" sx={{ mb: 2 }}>{notice}</Alert>}
          {envelope && (
            <>
              <Row label={t('esign.detail.document')}>{envelope.title}</Row>
              <Row label={t('esign.detail.signer')}>{signer ? `${signer.name} <${signer.email}>` : ''}</Row>
              <Row label={t('esign.detail.sentBy')}>{envelope.sender_name || ''}</Row>
              <Row label={t('esign.detail.sentAt')}>{formatDateTime(envelope.sent_at, lang)}</Row>
              {active && <Row label={t('esign.detail.expiresAt')}>{formatDateTime(envelope.expires_at, lang)}</Row>}
              {envelope.completed_at && <Row label={t('esign.detail.completedAt')}>{formatDateTime(envelope.completed_at, lang)}</Row>}
              {envelope.declined_reason && <Row label={t('esign.detail.declinedReason')}>{envelope.declined_reason}</Row>}
              {envelope.void_reason && <Row label={t('esign.detail.voidReason')}>{envelope.void_reason}</Row>}
              <Row label={t('esign.detail.fields')}>
                {t('esign.detail.fieldSummary', { signatures: count('signature'), initials: count('initials'), other: otherCount })}
              </Row>
              {envelope.reminder_count > 0 && (
                <Row label="">{t('esign.detail.remindersSent', { count: envelope.reminder_count })}</Row>
              )}
              <Row label={t('esign.detail.envelopeId')}>
                <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{envelope.public_id}</Typography>
              </Row>

              <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap', gap: 1 }}>
                <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={() => download('original')}>
                  {t('esign.detail.downloadOriginal')}
                </Button>
                {envelope.signed_s3_key && (
                  <Button size="small" variant="contained" color="success" startIcon={<DownloadIcon />} onClick={() => download('signed')}>
                    {t('esign.detail.downloadSigned')}
                  </Button>
                )}
              </Stack>

              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2">{t('esign.detail.timeline')}</Typography>
              <List dense disablePadding>
                {data.events.map((ev) => (
                  <ListItem key={ev.id} disableGutters>
                    <ListItemText
                      primary={t(`esign.events.${ev.event_type}`, { defaultValue: ev.event_type })}
                      secondary={[
                        formatDateTime(ev.created_at, lang),
                        ev.actor_name || ev.recipient_email,
                        ev.ip_address,
                        ev.details?.error,
                        ev.details?.reason,
                      ].filter(Boolean).join(' · ')}
                    />
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
          {active && (
            <>
              <Button color="error" startIcon={<VoidIcon />} onClick={() => setVoidOpen(true)} disabled={busy}>
                {t('esign.detail.void')}
              </Button>
              <Button startIcon={<SendIcon />} onClick={() => runAction('resend', {}, t('esign.detail.resent'))} disabled={busy}>
                {t('esign.detail.resend')}
              </Button>
            </>
          )}
          <Button onClick={onClose}>{t('esign.detail.close')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={voidOpen} onClose={() => setVoidOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{t('esign.detail.voidTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>{t('esign.detail.voidBody')}</Typography>
          <TextField
            label={t('esign.detail.voidReasonLabel')}
            value={voidReason}
            onChange={(e) => setVoidReason(e.target.value)}
            fullWidth
            multiline
            minRows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVoidOpen(false)}>{t('esign.detail.cancel')}</Button>
          <Button
            color="error"
            variant="contained"
            disabled={busy}
            onClick={async () => {
              setVoidOpen(false);
              await runAction('void', { reason: voidReason }, t('esign.detail.voided'));
              setVoidReason('');
            }}
          >
            {t('esign.detail.void')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EnvelopeDetailDialog;
